import React, { useEffect } from 'react';
import { X, ArrowRight, ShieldCheck, FileText, Users, ExternalLink } from 'lucide-react';
import { CommitteeInfo } from '../config/summitConfig';
import { CommitteeEmblem } from './CommitteeEmblems';

interface CommitteeModalProps {
  committee: CommitteeInfo | null;
  onClose: () => void;
  onSelectForRegistration?: (committeeName: string) => void;
  reducedMotion: boolean;
}

export const CommitteeModal: React.FC<CommitteeModalProps> = ({
  committee,
  onClose,
  onSelectForRegistration,
  reducedMotion,
}) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!committee) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 lg:p-10 bg-black/85 backdrop-blur-md transition-opacity duration-300">
      <div 
        className="relative w-full max-w-2xl bg-gradient-to-b from-[#181818] via-[#141414] to-[#0f0f0f] border border-bronze/50 rounded-sm shadow-2xl p-6 sm:p-10 transition-all duration-300 preserve-3d overflow-hidden"
        style={{
          boxShadow: '0 30px 80px rgba(0,0,0,0.9), inset 0 1px 0 rgba(232, 221, 200, 0.2)'
        }}
      >
        {/* Background Committee Heraldic Emblem */}
        <div 
          className="absolute -right-8 -bottom-8 w-64 h-64 sm:w-80 sm:h-80 pointer-events-none select-none opacity-[0.14] text-beige/[0.12] filter drop-shadow-[0_0_30px_rgba(212,175,55,0.2)]"
          aria-hidden="true"
        >
          <CommitteeEmblem id={committee.id} />
        </div>

        {/* Greek Key Top Banner */}
        <div className="w-full h-3 greek-key-border opacity-70 mb-6 relative z-10" />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 p-2 rounded-full border border-bronze/40 text-ivory/70 hover:text-beige hover:border-beige transition-colors focus:outline-none"
          aria-label="Close modal"
          data-cursor-interactive
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="border-b border-bronze/30 pb-6 mb-8">
          <div className="flex items-center gap-3 mb-2">
            <span className="font-mono text-xs font-bold text-bronze tracking-widest">
              CHAMBER {committee.number}
            </span>
            <span className="text-white/20">•</span>
            <span className="text-[10px] uppercase tracking-widest text-beige-300">
              {committee.category}
            </span>
            <span className="text-white/20">•</span>
            <span className="text-[10px] uppercase tracking-widest text-beige font-semibold px-2 py-0.5 bg-white/5 border border-bronze/30 rounded-sm">
              {committee.track === 'Auction' ? 'AUCTION TRACK' : 'CONVENTIONAL TRACK'}
            </span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ivory">
            {committee.abbreviation}
          </h2>
          {committee.fullName && 
           committee.fullName.toLowerCase() !== committee.name.toLowerCase() && 
           committee.fullName.toLowerCase() !== committee.abbreviation.toLowerCase() && (
            <h3 className="text-sm uppercase tracking-widest text-beige-400 font-semibold mt-1">
              {committee.fullName}
            </h3>
          )}
        </div>

        {/* Committee Description */}
        <div className="mb-8">
          <h4 className="text-[10px] uppercase tracking-architectural text-bronze font-semibold mb-2">
            MANDATE & SIMULATION OVERVIEW
          </h4>
          <p className="text-sm sm:text-base text-ivory/80 font-light leading-relaxed">
            {committee.description}
          </p>
        </div>

        {/* Agenda & Executive Board Cards (Required to display "TO BE ANNOUNCED") */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {/* Agenda Card */}
          <div className="p-4 bg-charcoal-subtle/80 border border-bronze/30 rounded-sm flex items-start gap-3">
            <div className="p-2 border border-bronze/40 rounded text-bronze">
              <FileText className="w-4 h-4" />
            </div>
            <div>
              <span className="text-[9px] uppercase tracking-widest text-ivory/50 block font-semibold mb-1">
                COMMITTEE AGENDA
              </span>
              <p className="text-xs text-beige font-serif font-medium leading-relaxed block">
                {committee.agenda}
              </p>
              <span className="text-[10px] text-ivory/40 mt-1 block">
                Background guide releasing prior to conference.
              </span>
            </div>
          </div>

          {/* Executive Board Card */}
          <div className="p-4 bg-charcoal-subtle/80 border border-bronze/30 rounded-sm flex items-start gap-3">
            <div className="p-2 border border-bronze/40 rounded text-bronze">
              <Users className="w-4 h-4" />
            </div>
            <div>
              <span className="text-[9px] uppercase tracking-widest text-ivory/50 block font-semibold">
                EXECUTIVE BOARD
              </span>
              <span className="text-xs uppercase tracking-widest text-beige font-mono font-bold mt-0.5 block">
                {committee.executiveBoard}
              </span>
              <span className="text-[10px] text-ivory/40 mt-1 block">
                Chairperson and Co-Chair announcement soon.
              </span>
            </div>
          </div>
        </div>

        {/* Modal Action Buttons */}
        <div className="pt-6 border-t border-bronze/30 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
            <a
              href={committee.formUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3.5 bg-gradient-to-r from-beige-300 via-beige to-beige-400 text-deepblack text-xs uppercase tracking-widest font-semibold rounded-sm border border-beige hover:shadow-glow-bronze transition-all flex items-center justify-center gap-2 group"
              data-cursor-interactive
              data-cursor-text="APPLY"
            >
              <span>REGISTER FOR THIS COMMITTEE</span>
              <ExternalLink className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>

            <button
              type="button"
              onClick={() => {
                onClose();
                const el = document.getElementById('matrices');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-4 py-3.5 border border-bronze/40 text-beige hover:border-beige hover:bg-white/5 text-xs uppercase tracking-widest font-mono rounded-sm transition-colors text-center"
            >
              VIEW MATRICES ↓
            </button>
          </div>

          <button
            onClick={onClose}
            className="w-full sm:w-auto px-6 py-3 text-xs tracking-widest uppercase text-ivory/70 hover:text-beige transition-colors text-center"
          >
            CLOSE ×
          </button>
        </div>

      </div>
    </div>
  );
};
